import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card,
  CardContent,
  Container,
  Paper,
  Autocomplete,
  TextField
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import FloridaTrialsMapComplete from './maps/FloridaTrialsMapComplete';
import SystemOverviewTabs from '../../components/system/SystemOverviewTabs';
import CognitoTest from '../../components/CognitoTest';
import AmplifyConfigTest from '../../components/AmplifyConfigTest';
import DirectAmplifyTest from '../../components/DirectAmplifyTest';
import ConfigValidator from '../../components/ConfigValidator';

// MammoTrials Landing Page - Updated for breast cancer clinical trials

const LandingPage = ({ onExploreTopic, activeTopic }) => {
  const { openAuthModal } = useAuth();
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [resetViewTrigger, setResetViewTrigger] = useState(0);

  const allCounties = [
    'Miami-Dade', 'Broward', 'Palm Beach', 'Hillsborough', 'Orange',
    'Pinellas', 'Duval', 'Lee', 'Polk', 'Volusia',
    'Alachua', 'Baker', 'Bay', 'Bradford', 'Brevard', 'Calhoun', 'Charlotte',
    'Citrus', 'Clay', 'Collier', 'Columbia', 'DeSoto', 'Dixie', 'Escambia',
    'Flagler', 'Franklin', 'Gadsden', 'Gilchrist', 'Glades', 'Gulf',
    'Hamilton', 'Hardee', 'Hendry', 'Hernando', 'Highlands', 'Holmes',
    'Indian River', 'Jackson', 'Jefferson', 'Lafayette', 'Lake', 'Leon',
    'Levy', 'Liberty', 'Madison', 'Manatee', 'Marion', 'Martin', 'Monroe',
    'Nassau', 'Okaloosa', 'Okeechobee', 'Osceola', 'Pasco', 'Putnam',
    'Santa Rosa', 'Sarasota', 'Seminole', 'St. Johns', 'St. Lucie', 'Sumter',
    'Suwannee', 'Taylor', 'Union', 'Wakulla', 'Walton', 'Washington'
  ];

  const heroPillars = [
    {
      title: 'Hope Through Intelligent Matching 🎯',
      text: 'Your cancer, your perfect trial match. AI matching considers molecular profile, stage, location, and life context with clear reasons for every match.'
    },
    {
      title: 'Removing the Research Burden 💪',
      text: 'We find the trials, you focus on healing. Patients get digestible summaries and visual participation timelines instead of dense protocols.'
    },
    {
      title: 'Community & Shared Experience 🤝',
      text: 'Patient stories, peer connections, trial-veteran mentorship, and SDOH support for transportation, childcare, and insurance navigation.'
    },
    {
      title: 'Transparency & Control 👁️',
      text: 'No hidden agendas. The platform is clear about sponsor funding, and patients keep data control with the ability to withdraw anytime.'
    },
    {
      title: 'Dignity & Empowerment 👑',
      text: 'Patients are partners in care, not numbers. Personalized guidance, educational tools, and symptom tracking support informed decisions.'
    },
    {
      title: 'Practical Life Integration 🏠',
      text: 'Trial options are evaluated in real-life context including work, family, finances, scheduling flexibility, and care-team coordination.'
    },
    {
      title: 'Authentic Communication 💬',
      text: 'Human language, realistic hope, honest risk conversations, regular progress updates, and direct access to coordinators and nurse navigators.'
    },
    {
      title: 'You Are Not Alone ❤️',
      text: 'The promise is simple: you do not have to navigate this alone, and we help you find the best possible chance for the outcome you want.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box sx={{ 
        position: 'relative', 
        overflow: 'hidden', 
        background: 'linear-gradient(90deg, #1976d2 0%, #9c27b0 50%, #4caf50 100%)',
        color: 'white'
      }}>
        <Box sx={{ 
          position: 'absolute', 
          inset: 0, 
          bgcolor: 'rgba(0,0,0,0.1)' 
        }} />
        
        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <Box sx={{ position: 'absolute', top: 40, left: 40, width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <Box sx={{ position: 'absolute', top: 128, right: 80, width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <Box sx={{ position: 'absolute', bottom: 80, left: 128, width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <Box sx={{ position: 'absolute', bottom: 128, right: 64, width: 96, height: 96, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
        </Box>
        
        <Container maxWidth="lg" sx={{ position: 'relative', py: 12 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
              <Paper sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                backdropFilter: 'blur(8px)', 
                p: 3, 
                mr: 3,
                border: '1px solid rgba(255,255,255,0.3)'
              }}>
                <Typography variant="h2">🩺</Typography>
              </Paper>
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h2" component="h1" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '2.5rem', md: '4rem' } }}>
                  MammoTrials
                </Typography>
                <Typography variant="h5" sx={{ color: 'yellow.200', fontWeight: 600 }}>
                  Hope Through Intelligent Matching
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="h5" sx={{ mb: 6, maxWidth: '800px', mx: 'auto', lineHeight: 1.4, fontWeight: 500, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              “Your cancer, your perfect trial match.” We combine AI-powered matching with human support so you can focus on healing while we help identify the best-fit clinical trial options across Florida.
            </Typography>

            <Typography variant="h6" sx={{ mb: 5, maxWidth: '980px', mx: 'auto', lineHeight: 1.6, fontWeight: 500, opacity: 0.95 }}>
              You deserve to feel seen, supported, and empowered—not processed through a system.
            </Typography>

            <Grid container spacing={2.5} sx={{ mb: 6 }}>
              {heroPillars.map((pillar) => (
                <Grid item xs={12} md={6} key={pillar.title}>
                  <Paper
                    sx={{
                      height: '100%',
                      p: 2.5,
                      textAlign: 'left',
                      bgcolor: 'rgba(255,255,255,0.16)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.35)',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      {pillar.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95, lineHeight: 1.5 }}>
                      {pillar.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{
              bgcolor: 'white',
              borderRadius: 3,
              boxShadow: 8,
              p: { xs: 4, md: 5 },
              border: 2,
              borderColor: 'grey.200',
              mb: 6
            }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                  MammoTrials Platform Impact
                </Typography>
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Advancing breast cancer research and patient care across Florida
                </Typography>
              </Box>
              <Grid container spacing={4}>
                {[
                  { number: '150+', label: 'Active Clinical Trials', color: 'primary.main' },
                  { number: '45+', label: 'Research Centers', color: 'success.main' },
                  { number: '3,500+', label: 'Patients Enrolled', color: 'secondary.main' },
                  { number: '92%', label: 'Match Success Rate', color: 'error.main' }
                ].map((stat, index) => (
                  <Grid item xs={12} md={3} key={index}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h2" sx={{ fontWeight: 'bold', color: stat.color, mb: 1.5 }}>
                        {stat.number}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1.1rem' }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
              
            <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
              <Grid item>
                <Button 
                  onClick={() => openAuthModal('signup')}
                  variant="contained"
                  size="large"
                  sx={{ 
                    background: 'linear-gradient(45deg, #ffeb3b 0%, #ff9800 100%)',
                    color: 'rgba(0,0,0,0.87)',
                    fontWeight: 'bold',
                    px: 6,
                    py: 2.5,
                    fontSize: '1.1rem',
                    boxShadow: 4,
                    '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
                    transition: 'all 0.3s',
                    minWidth: 280
                  }}
                  startIcon={<Typography>🩺</Typography>}
                >
                  Patient Portal
                </Button>
              </Grid>
              <Grid item>
                <Button 
                  onClick={() => openAuthModal('signup')}
                  variant="contained"
                  size="large"
                  sx={{ 
                    background: 'linear-gradient(45deg, #2196f3 0%, #21cbf3 100%)',
                    fontWeight: 'bold',
                    px: 6,
                    py: 2.5,
                    fontSize: '1.1rem',
                    boxShadow: 4,
                    '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
                    transition: 'all 0.3s',
                    minWidth: 280
                  }}
                  startIcon={<Typography>⚕️</Typography>}
                >
                  Clinical Trials Portal
                </Button>
              </Grid>
              <Grid item>
                <Button 
                  onClick={() => openAuthModal('signup')}
                  variant="contained"
                  size="large"
                  sx={{ 
                    background: 'linear-gradient(45deg, #4caf50 0%, #8bc34a 100%)',
                    fontWeight: 'bold',
                    px: 6,
                    py: 2.5,
                    fontSize: '1.1rem',
                    boxShadow: 4,
                    '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
                    transition: 'all 0.3s',
                    minWidth: 280
                  }}
                  startIcon={<Typography>🏥</Typography>}
                >
                  Healthcare Portal
                </Button>
              </Grid>
            </Grid>
            
            <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 800, mx: 'auto' }}>
              {[
                { number: '150+', label: 'Active Clinical Trials' },
                { number: '45+', label: 'Research Centers' },
                { number: '67', label: 'Florida Counties' }
              ].map((stat, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Paper sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    backdropFilter: 'blur(8px)', 
                    p: 4, 
                    textAlign: 'center',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white'
                  }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body1" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, opacity: 0.9 }}>
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* System Overview & Role Hierarchy */}
      <Box sx={{ py: 4, bgcolor: 'white', borderTop: 1, borderColor: 'grey.100' }}>
        <SystemOverviewTabs onExploreTopic={onExploreTopic} activeTopic={activeTopic} />
      </Box>

      {/* Cognito Authentication Test (Development Only) */}
      {process.env.NODE_ENV === 'development' && process.env.REACT_APP_SHOW_AUTH_DEBUG === 'true' && (
        <Box sx={{ py: 4, bgcolor: 'error.light' }}>
          <Container maxWidth="xl">
            <Typography variant="h4" gutterBottom color="error.dark" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '2rem' }}>
              🚨 ACTIVE TROUBLESHOOTING: Auth UserPool Configuration Issue
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: 'center', color: 'error.dark' }}>
              Multiple diagnostic tools to isolate the "Auth UserPool not configured" error:
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DirectAmplifyTest />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConfigValidator />
              </Grid>
              <Grid item xs={12} md={4}>
                <AmplifyConfigTest />
              </Grid>
              <Grid item xs={12} md={4}>
                <CognitoTest />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
                  <Typography variant="subtitle2" gutterBottom color="info.main">
                    📝 Expected Credentials
                  </Typography>
                  <Typography variant="caption" component="div">
                    <strong>Email:</strong> patient.test@mammotrial.dev<br/>
                    <strong>Password:</strong> TestPatient123!<br/>
                    <strong>User Pool:</strong> us-east-1_tzvp43bdI<br/>
                    <strong>Client ID:</strong> 6fb8iv25uapd4jq7s23p7872bj
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Troubleshooting Steps:</strong><br/>
                1. First run "Test 1: Direct Config" (red box) - this bypasses all our code<br/>
                2. Check "Validate Configuration" (yellow box) - verifies config object structure<br/>
                3. Compare results to identify where the issue occurs
              </Typography>
            </Alert>
          </Container>
        </Box>
      )}

        {/* County Selection Section */}
        <Box sx={{ py: 4, bgcolor: 'white' }}>
          <Container maxWidth="lg">
            {/* Map Section */}
            <Box sx={{ mt: 0 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                  🗺️ Interactive Clinical Trials Map
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Explore breast cancer clinical trials, research centers, and mCODE-enabled facilities across Florida
                </Typography>
              </Box>
              
              <Paper sx={{ p: 3, boxShadow: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ position: 'relative', width: '100%', height: mapExpanded ? 850 : 600 }}>
                  <FloridaTrialsMapComplete
                    selectedCounty={selectedCounty}
                    resetViewTrigger={resetViewTrigger}
                    userRole="public"
                    accessLevel={1}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, gap: 2, flexWrap: 'wrap' }}>
                  <Autocomplete
                    options={allCounties}
                    value={selectedCounty}
                    onChange={(_, value) => setSelectedCounty(value || null)}
                    sx={{ minWidth: 280, flexGrow: 1, maxWidth: 420, bgcolor: 'white' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search by county"
                        placeholder="Type county name"
                        size="small"
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained"
                      onClick={() => {
                        setSelectedCounty(null);
                        setResetViewTrigger((value) => value + 1);
                      }}
                      sx={{ 
                        background: 'linear-gradient(45deg, #1976d2 0%, #2196f3 100%)',
                        '&:hover': { transform: 'scale(1.02)' }
                      }}
                    >
                      Reset View
                    </Button>
                    <Button 
                      variant="contained" 
                      color="secondary"
                      onClick={() => setMapExpanded((expanded) => !expanded)}
                      sx={{ '&:hover': { transform: 'scale(1.02)' } }}
                    >
                      {mapExpanded ? 'Collapse Map' : 'Expand Map'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ mt: 8 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                  🧬 mCODE Interactive Graph
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Interactive visualization of mCODE profiles and relationships
                </Typography>
              </Box>

              <Paper sx={{ p: 2, boxShadow: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ position: 'relative', width: '100%', height: 900, borderRadius: 2, overflow: 'hidden' }}>
                  <iframe
                    title="mCODE Interactive Graph"
                    src="/mcode_complete.html"
                    style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
                  />
                </Box>
              </Paper>
            </Box>
          </Container>
        </Box>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: 'grey.900', color: 'white', borderTop: 1, borderColor: 'grey.800' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.95 }}>
            © {new Date().getFullYear()} Chears LLC. All rights reserved. Contact:{' '}
            <Box
              component="a"
              href="mailto:chait@chears.health"
              sx={{ color: 'inherit', textDecoration: 'underline' }}
            >
              chait@chears.health
            </Box>
          </Typography>
        </Container>
      </Box>

    </>
  );
};

export default LandingPage;